"use client"

import React, { useRef, useMemo, memo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import FormSelectInput from '../../../../shared/components/form-inputs/form-select-input'
import FormMultiSelectInput from '../../../../shared/components/form-inputs/form-multi-select-input'
import { Button } from '../../../../shared/components/ui/button'
import {
    getHomeOwnerQueryParamsSchema,
    GetHomeOwnerQueryParamsType
} from '../../schema/home/query-params.schema'
import { DialogClose } from '@/shared/components/ui/dialog'
import { buildUrl } from '@/shared/lib/utils'
import { useRouter, usePathname } from 'next/navigation'
import { useCurrentSearchParams } from '@/shared/hooks/use-current-search-params'
import { useSearchParams } from 'next/navigation'
import { homeOwnerAgeOptions, yesNoOptions } from '@/shared/constants/options'
import FormTextInput from '@/shared/components/form-inputs/form-text-input'
import { residenceTypeOptions, structureTypeOptions, overallStatusSelectableOptions, workScopeStatusOptions, kindOfRoofWorkOptions, goodwillStatusOptions } from '../../constants/options'
import { normalizeOverallStatusList } from '../../lib/overall-status.utils'
import { useWorkScopes } from '@/features/work-orders/hooks/use-work-scopes'
import { WORK_SCOPE_GOODWILL_OPTIONS, getGoodwillOptionsForRegion } from '../../lib/workscope-goodwill-filter'
import { useRegion } from '@/shared/hooks/use-region'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { useFilterSearchHomes, FilterSearchHomesResponse } from '../../hooks/filter-search-homes.hook'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import { useClaimedByOptions } from '../../hooks/use-claimed-by-options'

type HomeOwnerFilterFormProps = {
    onSuccessCallback?: () => void,
}

/** Placeholder style: same font as labels, more transparent in the box */
const FILTER_PLACEHOLDER_CLASS = "text-sm text-muted-foreground/55"
const VOLUNTEER_HIDDEN_DEMOGRAPHIC_KEYS = new Set([
    'medicalNeeds',
    'homeownerAge',
    'isSingleParent',
    'numChildrenUnder18',
    'isFirstResponder',
    'isVeteran',
])

const HomeOwnerFilterForm = memo(function HomeOwnerFilterForm({ onSuccessCallback }: HomeOwnerFilterFormProps) {
    const { session } = useSession()
    const isVolunteer = session?.role === UserRole.VOLUNTEER
    const searchParams = useSearchParams()
    const tabParam = searchParams.get('tab')
    const isSubmittingRef = useRef(false)
    const { regionId, isLoading: regionLoading } = useRegionId()
    const { regionSlug } = useRegion()
    const shouldFetchHomes = (regionSlug != null && regionSlug.trim() !== '') || (!regionLoading && regionId != null)
    const { data: homesData } = useFilterSearchHomes(
        regionId ?? undefined,
        true,
        { enabled: shouldFetchHomes, regionSlug: regionSlug ?? undefined, initialLimit: 50 }
    )
    const cityOptions = useMemo(() => {
        if (!homesData || !(homesData as FilterSearchHomesResponse).success || !Array.isArray((homesData as FilterSearchHomesResponse).data)) return []
        const data = (homesData as FilterSearchHomesResponse).data
        const cities = new Set<string>()
        data.forEach((home: { city?: string | null }) => {
            const c = home.city?.trim()
            if (c) cities.add(c)
        })
        return Array.from(cities).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).map((c) => ({ value: c, label: c }))
    }, [homesData])
    const countyOptions = useMemo(() => {
        if (!homesData || !(homesData as FilterSearchHomesResponse).success || !Array.isArray((homesData as FilterSearchHomesResponse).data)) return []
        const data = (homesData as FilterSearchHomesResponse).data
        const counties = new Set<string>()
        data.forEach((home: { county?: string | null }) => {
            const c = home.county?.trim()
            if (c) counties.add(c)
        })
        return Array.from(counties).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).map((c) => ({ value: c, label: c }))
    }, [homesData])
    const { workScopeOptions } = useWorkScopes()
    const { options: claimedByOptions } = useClaimedByOptions()
    const workscopeGoodwillOptions = useMemo(
        () => {
            if (!Array.isArray(workScopeOptions)) return []
            return getGoodwillOptionsForRegion(workScopeOptions.map((o) => o.value))
        },
        [workScopeOptions]
    )
    
    
    const currentParams = useCurrentSearchParams<GetHomeOwnerQueryParamsType>([
        "reliefStatus",
        "rebuildingStatus",
        "goodwillStatus",
        "overallStatus",
        "workscope",
        "claimedBy",
        "workscopeStatus",
        "workscopeGoodwill",
        "workscopeGoodwillStatus",
        "kindOfRoofWork",
        "medicalNeeds",
        "homeownerAge",
        "isSingleParent",
        "numChildrenUnder18",
        "isFirstResponder",
        "isVeteran",
        "hasFloodInsurance",
        "hasHomeInsurance",
        "femaStatus",
        "sbaStatus",
        "residenceType",
        "structureType",
        "assessmentCompleted",
        "activeStatus",
        "city",
        "county",
    ])

    // Normalize overallStatus, workscope, roofing, kindOfRoofWork, residentType, structureType, and assessmentCompleted to always be arrays
    const normalizedParams = useMemo(() => ({
        ...currentParams,
        overallStatus: Array.isArray(currentParams.overallStatus) 
            ? normalizeOverallStatusList(currentParams.overallStatus as string[])
            : currentParams.overallStatus 
                ? normalizeOverallStatusList((currentParams.overallStatus as string).split(','))
                : [],
        workscope: Array.isArray(currentParams.workscope) 
            ? currentParams.workscope 
            : currentParams.workscope 
                ? (currentParams.workscope as string).split(',') 
                : [],
        claimedBy: Array.isArray(currentParams.claimedBy)
            ? currentParams.claimedBy
            : currentParams.claimedBy
                ? (currentParams.claimedBy as string).split(',').map((s: string) => s.trim()).filter(Boolean)
                : [],
        workscopeStatus: Array.isArray(currentParams.workscopeStatus) 
            ? currentParams.workscopeStatus 
            : currentParams.workscopeStatus 
                ? (currentParams.workscopeStatus as string).split(',') 
                : [],
        workscopeGoodwill: Array.isArray(currentParams.workscopeGoodwill) 
            ? currentParams.workscopeGoodwill 
            : currentParams.workscopeGoodwill 
                ? (currentParams.workscopeGoodwill as string).split(',') 
                : [],
        workscopeGoodwillStatus: Array.isArray(currentParams.workscopeGoodwillStatus) 
            ? currentParams.workscopeGoodwillStatus 
            : currentParams.workscopeGoodwillStatus 
                ? (currentParams.workscopeGoodwillStatus as string).split(',') 
                : [],
        reliefStatus: Array.isArray(currentParams.reliefStatus) 
            ? currentParams.reliefStatus 
            : currentParams.reliefStatus 
                ? (currentParams.reliefStatus as string).split(',') 
                : [],
        rebuildingStatus: Array.isArray(currentParams.rebuildingStatus) 
            ? currentParams.rebuildingStatus 
            : currentParams.rebuildingStatus 
                ? (currentParams.rebuildingStatus as string).split(',') 
                : [],
        goodwillStatus: Array.isArray(currentParams.goodwillStatus) 
            ? currentParams.goodwillStatus 
            : currentParams.goodwillStatus 
                ? (currentParams.goodwillStatus as string).split(',') 
                : [],
        kindOfRoofWork: Array.isArray(currentParams.kindOfRoofWork) 
            ? currentParams.kindOfRoofWork 
            : currentParams.kindOfRoofWork 
                ? (currentParams.kindOfRoofWork as string).split(',') 
                : [],
        residenceType: Array.isArray(currentParams.residenceType) 
            ? currentParams.residenceType 
            : currentParams.residenceType 
                ? (currentParams.residenceType as string).split(',') 
                : [],
        structureType: Array.isArray(currentParams.structureType) 
            ? currentParams.structureType 
            : currentParams.structureType 
                ? (currentParams.structureType as string).split(',') 
                : [],
        assessmentCompleted: Array.isArray(currentParams.assessmentCompleted) 
            ? currentParams.assessmentCompleted 
            : currentParams.assessmentCompleted 
                ? (currentParams.assessmentCompleted as string).split(',') 
                : [],
        activeStatus: (currentParams.activeStatus === 'active' || currentParams.activeStatus === 'inactive') ? currentParams.activeStatus : 'all',
        city: Array.isArray(currentParams.city) 
            ? currentParams.city 
            : currentParams.city 
                ? (currentParams.city as string).split(',').map((s: string) => s.trim()).filter(Boolean) 
                : [],
        county: Array.isArray(currentParams.county) 
            ? currentParams.county 
            : currentParams.county 
                ? (currentParams.county as string).split(',').map((s: string) => s.trim()).filter(Boolean) 
                : [],
    }), [currentParams])

    const form = useForm<GetHomeOwnerQueryParamsType>({
        resolver: zodResolver(getHomeOwnerQueryParamsSchema),
        mode: "onSubmit",
        defaultValues: normalizedParams as GetHomeOwnerQueryParamsType,
    })
    const { control, formState: { errors, isSubmitting } } = form
    const router = useRouter()
    const pathname = usePathname()

    const onSubmit = (data: GetHomeOwnerQueryParamsType) => {
        // Prevent multiple submissions using ref
        if (isSubmittingRef.current) {
            return
        }
        isSubmittingRef.current = true

        try {
            // Filter out undefined values and only include filter parameters
            const filterData = Object.fromEntries(
                Object.entries(data).filter(([key, value]) => 
                    !(isVolunteer && VOLUNTEER_HIDDEN_DEMOGRAPHIC_KEYS.has(key)) &&
                    value !== undefined && 
                    value !== null && 
                    value !== '' && 
                    !(Array.isArray(value) && value.length === 0) && // Filter out empty arrays
                    !['page', 'limit'].includes(key) &&
                    (key !== 'activeStatus' || (value !== 'all' && value !== ''))
                ).map(([key, value]) => {
                    // Convert arrays to comma-separated strings for multi-select fields
                    if (Array.isArray(value)) {
                        return [key, value.join(',')]
                    }
                    return [key, value]
                })
            )
            
            const url = buildUrl(
                pathname,
                filterData,
                {
                    previousParams: {
                        page: 1,
                        limit: 50,
                        ...(tabParam && { tab: tabParam })
                    }
                }
            )
            
            onSuccessCallback?.()
            router.push(url)
            isSubmittingRef.current = false
        } catch (error) {
            console.error("Error building URL or pushing to router:", error)
            isSubmittingRef.current = false
        }
    }

    const onError = (errors: any) => {
        console.log("Form validation errors:", errors)
    }

    return (
        <FormProvider {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onError)}
                className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start'
            >
                <FormMultiSelectInput
                    name='reliefStatus'
                    options={goodwillStatusOptions}
                    placeholder='Select status...'
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    showSelectAll
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Relief Status"
                    }}
                    control={control}
                />
                <FormMultiSelectInput
                    name='rebuildingStatus'
                    options={goodwillStatusOptions}
                    placeholder='Select status...'
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    showSelectAll
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Rebuilding Status"
                    }}
                    control={control}
                />
                <FormMultiSelectInput
                    name='goodwillStatus'
                    options={goodwillStatusOptions}
                    placeholder='Select status...'
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    showSelectAll
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Goodwill Status"
                    }}
                    control={control}
                />
                <FormMultiSelectInput
                    name='overallStatus'
                    options={overallStatusSelectableOptions}
                    placeholder='Select status...'
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    showSelectAll
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Overall Home Status"
                    }}
                    control={control}
                />
                <FormMultiSelectInput
                    name='workscope'
                    options={workScopeOptions}
                    placeholder='Select options...'
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    showSelectAll
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Workscope"
                    }}
                    control={control}
                />
                <FormMultiSelectInput
                    name='claimedBy'
                    options={claimedByOptions}
                    placeholder='Select organizations...'
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    showSelectAll
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Claimed By"
                    }}
                    control={control}
                />
                <FormMultiSelectInput
                    name='workscopeStatus'
                    options={workScopeStatusOptions}
                    placeholder='Select status...'
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    showSelectAll
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Status of Workscope"
                    }}
                    control={control}
                />
                <FormMultiSelectInput
                    name='workscopeGoodwill'
                    options={workscopeGoodwillOptions}
                    placeholder='Select options...'
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    showSelectAll
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Goodwill Workscope"
                    }}
                    control={control}
                />
                <FormMultiSelectInput
                    name='workscopeGoodwillStatus'
                    options={workScopeStatusOptions}
                    placeholder='Select status...'
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    showSelectAll
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Status of Goodwill Workscope"
                    }}
                    control={control}
                />
                <FormMultiSelectInput
                    name='kindOfRoofWork'
                    options={kindOfRoofWorkOptions}
                    placeholder='Select roof repair type...'
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "What kind of roof repair?"
                    }}
                    control={control}
                />
                {!isVolunteer && (
                    <FormSelectInput
                        name='medicalNeeds'
                        size="sm"
                        className="rounded-full"
                        placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                        inputGroupProps={{
                            wrapperClassName: 'w-full min-w-0',
                            label: "Special (Medical) Needs"
                        }}
                        options={yesNoOptions}
                        control={control}
                    />
                )}
                {!isVolunteer && (
                    <FormSelectInput
                        name='homeownerAge'
                        size="sm"
                        className="rounded-full"
                        placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                        inputGroupProps={{
                            wrapperClassName: 'w-full min-w-0',
                            label: "Homeowner Age"
                        }}
                        options={homeOwnerAgeOptions}
                        control={control}
                    />
                )}
                {!isVolunteer && (
                    <FormSelectInput
                        name='isSingleParent'
                        size="sm"
                        className="rounded-full"
                        placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                        control={control}
                        inputGroupProps={{
                            wrapperClassName: 'w-full min-w-0',
                            label: "Single Parent/ Grandparent"
                        }}
                        options={yesNoOptions}
                    />
                )}
                {!isVolunteer && (
                    <FormSelectInput
                        name='numChildrenUnder18'
                        size="sm"
                        className="rounded-full"
                        placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                        control={control}
                        inputGroupProps={{
                            wrapperClassName: 'w-full min-w-0',
                            label: "Children Under 18 in Home"
                        }}
                        options={yesNoOptions}
                    />
                )}
                {!isVolunteer && (
                    <FormSelectInput
                        name='isFirstResponder'
                        size="sm"
                        className="rounded-full"
                        placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                        control={control}
                        inputGroupProps={{
                            wrapperClassName: 'w-full min-w-0',
                            label: "First Responder in home"
                        }}
                        options={yesNoOptions}
                    />
                )}
                {!isVolunteer && (
                    <FormSelectInput
                        name='isVeteran'
                        size="sm"
                        className="rounded-full"
                        placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                        control={control}
                        inputGroupProps={{
                            wrapperClassName: 'w-full min-w-0',
                            label: "Veteran in home"
                        }}
                        options={yesNoOptions}
                    />
                )}
                <FormMultiSelectInput
                    name='residenceType'
                    control={control}
                    options={residenceTypeOptions}
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Residence Type"
                    }}
                />
                <FormMultiSelectInput
                    name='structureType'
                    control={control}
                    options={structureTypeOptions}
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Structure Type"
                    }}
                />
                <FormMultiSelectInput
                    name='assessmentCompleted'
                    control={control}
                    options={[
                        { value: 'No Need', label: 'No Assistance Required' },
                        { value: 'Needed Not Started', label: 'Needed Not Started' },
                        { value: 'Reassessment Needed', label: 'Reassessment Needed' },
                        { value: 'Complete', label: 'Complete' }
                    ]}
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Assessment Completed"
                    }}
                />
                <FormSelectInput
                    name='activeStatus'
                    size="sm"
                    className="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    emptyValue="all"
                    control={control}
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Active / Inactive"
                    }}
                    options={[
                        { value: 'all', label: 'Select' },
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' }
                    ]}
                />
                <FormMultiSelectInput
                    name='city'
                    control={control}
                    options={cityOptions}
                    placeholder='Select cities...'
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "City"
                    }}
                />
                <FormMultiSelectInput
                    name='county'
                    control={control}
                    options={countyOptions}
                    placeholder='Select counties...'
                    triggerClassName="rounded-full"
                    placeholderClassName={FILTER_PLACEHOLDER_CLASS}
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "County"
                    }}
                />
                {Object.keys(errors).length > 0 && (
                    <div className="col-span-full p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600 font-medium">Please fix the following errors:</p>
                        <ul className="text-sm text-red-600 mt-1">
                            {Object.entries(errors).map(([field, error]) => (
                                <li key={field}>• {(error as any)?.message || `Error in ${field}`}</li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="grid grid-cols-[auto_1fr] gap-3 col-span-full pt-3">
                    <DialogClose asChild>
                        <Button
                            variant={'outline'}
                            type='button'
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type='submit'
                        className='w-full'
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Applying...' : 'Apply'}
                    </Button>
                </div>
                <div className="col-span-full pt-2">
                    <Button
                        variant={'ghost'}
                        type='button'
                        className='w-full text-sm'
                        onClick={() => {
                            form.reset({})
                            const url = buildUrl(
                                pathname,
                                {},
                                {
                                    previousParams: {
                                        page: "1",
                                        limit: "50",
                                        ...(tabParam && { tab: tabParam })
                                    }
                                }
                            )
                            router.push(url)
                            onSuccessCallback?.()
                        }}
                    >
                        Clear all filters
                    </Button>
                </div>
            </form>
        </FormProvider>
    )
})

export default HomeOwnerFilterForm
